/* ============================================================================
 * groupShape.js — reconcile stored form data with vue-formulate's schema groups
 * ----------------------------------------------------------------------------
 * vue-formulate v2 models EVERY schema `type: 'group'` as an ARRAY of element
 * objects — repeatable or not. It hydrates a group's inputs only when the bound
 * value is that array shape; a plain object (how most groups are stored in the
 * DB, inherited from the original Firestore app) is silently ignored, so the
 * fields render blank even though the data is present.
 *
 *   toFormValues  — on load:  wrap every group value as an array so it hydrates
 *                             (recurses nested groups).
 *   toStoreValues — on save:  restore each group's ORIGINAL stored shape
 *                             (object vs array), using the pre-edit data as the
 *                             oracle; new groups fall back to the schema's
 *                             `repeatable` marker. Round-trips losslessly, so an
 *                             untouched form saves back byte-for-byte identical.
 * ========================================================================== */
const isPlainObject = v => !!v && typeof v === 'object' && !Array.isArray(v)

function toFormValues(fields, data) {
  if (!Array.isArray(fields) || !isPlainObject(data)) return data
  const out = { ...data }
  fields.forEach(f => {
    if (!f || f.type !== 'group' || !Array.isArray(f.children) || !f.name) return
    if (!(f.name in out)) return // absent group: leave absent (renders empty)
    const v = out[f.name]
    const elems = Array.isArray(v) ? v : isPlainObject(v) ? [v] : []
    out[f.name] = elems.map(el =>
      toFormValues(f.children, isPlainObject(el) ? el : {})
    )
  })
  return out
}

function toStoreValues(fields, model, orig) {
  if (!Array.isArray(fields) || !isPlainObject(model)) return model
  const out = { ...model }
  fields.forEach(f => {
    if (!f || f.type !== 'group' || !Array.isArray(f.children) || !f.name) return
    if (!(f.name in out)) return
    const v = out[f.name]
    const modelElems = Array.isArray(v) ? v : isPlainObject(v) ? [v] : []
    const origVal = orig ? orig[f.name] : undefined
    const origElems = Array.isArray(origVal)
      ? origVal
      : isPlainObject(origVal)
      ? [origVal]
      : []
    // Prefer the shape this record was stored with; fall back to the schema
    // marker for groups that had no prior data.
    const wantArray = Array.isArray(origVal)
      ? true
      : isPlainObject(origVal)
      ? false
      : Object.prototype.hasOwnProperty.call(f, 'repeatable')
    const recursed = modelElems.map((el, i) =>
      toStoreValues(
        f.children,
        isPlainObject(el) ? el : {},
        origElems[i] !== undefined
          ? origElems[i]
          : origElems.length === 1
          ? origElems[0]
          : undefined
      )
    )
    out[f.name] = wantArray ? recursed : recursed.length ? recursed[0] : {}
  })
  return out
}

export { toFormValues, toStoreValues }
